import { type ReactElement } from 'react';
import { type DirectoryRealm } from '@vello/api-client';
import { Button, CountUp, DonutChart, Notice, Panel, SectionTitle, Stack, Text } from '@vello/web-theme';
import { statusTone } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useRead, type ReadState } from './use-read';

/** The base counts of one realm in the operator's scope: everything, active and suspended; the rest are deleted. */
export interface RealmCounts {
  readonly total: number;
  readonly active: number;
  readonly suspended: number;
}

/**
 * D513 (C-031): the head-count of one realm from the directory the screens already use — `POST /v1/ops/users/search` with
 * `limit=1` and the response's `total`, once per status. Never a list; the server clamps every read to the operator's scope,
 * and the country filter is the scoped one (the same as the directory screens').
 */
export function useRealmCounts(realm: DirectoryRealm): { state: ReadState<RealmCounts> | undefined; reload: () => void } {
  const { runtime, scope } = useAdmin();
  const country = scope.current.country;
  return useRead<RealmCounts>(async () => {
    const q = { realm, ...(country !== undefined ? { country } : {}), limit: 1 } as const;
    const [all, active, suspended] = await Promise.all([runtime.adminUsers.list(q), runtime.adminUsers.list({ ...q, status: 'ACTIVE' }), runtime.adminUsers.list({ ...q, status: 'SUSPENDED' })]);
    if (!all.ok) return all;
    if (!active.ok) return active;
    if (!suspended.ok) return suspended;
    return { ok: true, value: { total: all.value.total, active: active.value.total, suspended: suspended.value.total } };
  }, [runtime, realm, country]);
}

/**
 * A realm's accounts by status as a donut: active, suspended and the rest (deleted), the total counted up in the hole, a
 * legend that names every status with its count and share. A refused or failed read says so with a retry.
 */
export function StatusPanel({ title, totalLabel, state, reload }: { title: string; totalLabel: string; state: ReadState<RealmCounts> | undefined; reload: () => void }): ReactElement {
  const { t, lang } = useT();
  const nf = new Intl.NumberFormat(lang);
  const pf = new Intl.NumberFormat(lang, { style: 'percent', maximumFractionDigits: 1 });
  const ready = state?.status === 'ready' ? state.value : undefined;
  const slices =
    ready === undefined
      ? []
      : [
          { key: 'ACTIVE', label: t('dir.filter.active'), value: ready.active, tone: statusTone('ACTIVE') },
          { key: 'SUSPENDED', label: t('dir.filter.suspended'), value: ready.suspended, tone: statusTone('SUSPENDED') },
          { key: 'DELETED', label: t('dir.filter.deleted'), value: Math.max(0, ready.total - ready.active - ready.suspended), tone: 'neutral' as const },
        ];
  return (
    <Panel>
      <SectionTitle level={3} size="sub">{title}</SectionTitle>
      {state?.status === 'error' ? (
        <Stack gap={3} align="start">
          <Notice tone="danger">{t('pdash.error')}</Notice>
          <Button variant="secondary" onClick={reload}>{t('ui.retry')}</Button>
        </Stack>
      ) : (
        <DonutChart
          title={title}
          slices={slices}
          formatValue={(n) => nf.format(n)}
          formatShare={(n) => pf.format(n)}
          center={
            <>
              <CountUp value={ready?.total ?? 0} format={(n) => nf.format(n)} />
              <Text role="caption" tone="secondary">{totalLabel}</Text>
            </>
          }
          loading={state === undefined || state.status === 'loading'}
          loadingLabel={t('ui.loading')}
          emptyLabel={t('pdash.empty')}
          size={176}
        />
      )}
    </Panel>
  );
}
